import React, { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";
import { AlertTriangle } from "lucide-react";

// Componentes
import ReportedUserFilterToolbar from "./components/ReportedUserFilterToolbar";
import ReportedUserGrid from "./components/ReportedUserGrid";
import UserComplaintsModal from "./components/UserComplaintsModal";

// INTERFACES
export interface UserInfoApi {
  userId: number;
  name: string;
  lastName: string;
  userName: string;
  favoriteName: string | null;
  photoLink: string | null;
  gender: string | null;
  email: string | null;
  phoneNumber: string | null;
  phoneCode: string | null;
  emergencyContact: string | null;
  documents: any[];
  phones: any[];
  profilePhotos: any[];
  registerDate: string;
  registerDateDays: number | null;
  registerDateMonths: number | null;
  registerDateYears: number | null;
  cityName: string;
  status: string;
  isSuperHost: boolean;
}

export interface ReportedUserApi {
  reportedUserId: number;
  userInfo: UserInfoApi;
  complaintsCount: number;
  lastComplaintDate: string;
  complaints: any | null;
}

export interface ApiResponse {
  pagination: {
    totalItemCount: number;
    totalPageCount: number;
    pageSize: number;
    currentPage: number;
    hasPrevious: boolean;
    hasNext: boolean;
  };
  statusCode: string;
  data: ReportedUserApi[];
  hasError: boolean;
  resultError: any | null;
}

// Helper genérico
export const getInitials = (name: string, lastName: string) => {
  return `${name?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
};

const ReportedUsersPage = () => {
  const [reportedUsers, setReportedUsers] = useState<ReportedUserApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<ApiResponse["pagination"] | null>(null);

  // Estados locales para filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("mostReported");

  // ESTADOS PARA EL MODAL
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string | null>(null);

  const fetchReportedUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        pageNumber: currentPage.toString(),
        pageSize: "20"
      });

      const response = await apiClient.get<ApiResponse>(`Complaint/ReportedUsers?${params.toString()}`);
      
      let rawData = response.data || [];

      // FILTROS LOCALES
      if (searchTerm.trim()) {
        const lowerSearch = searchTerm.toLowerCase();
        rawData = rawData.filter(item => 
          item.userInfo.name.toLowerCase().includes(lowerSearch) || 
          item.userInfo.lastName.toLowerCase().includes(lowerSearch)
        );
      }

      if (sortOrder === "recentComplaint") {
        rawData.sort((a, b) => new Date(b.lastComplaintDate).getTime() - new Date(a.lastComplaintDate).getTime());
      } else {
        // mostReported (por defecto)
        rawData.sort((a, b) => b.complaintsCount - a.complaintsCount);
      }

      setReportedUsers(rawData);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error cargando usuarios denunciados:", error);
      setReportedUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReportedUsers(); }, [currentPage, searchTerm, sortOrder]);

  const openDrawer = (user: ReportedUserApi) => {
    console.log("Abrir detalle del usuario:", user.userInfo.name);
    // Lógica futura para abrir modal de detalle
  };

  const openActionModal = (user: ReportedUserApi, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedUserId(user.reportedUserId); 
    setSelectedUserName(`${user.userInfo.name} ${user.userInfo.lastName}`);
    setModalOpen(true);
  };

  return (
    <div className="flex w-full h-screen bg-gray-50 text-gray-900 font-sans overflow-hidden">
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* TOPBAR */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 md:px-[32px] py-3 md:py-4 border-b border-gray-200 bg-white z-20 shrink-0 gap-3 sm:gap-0">
          <div>
            <h1 className="text-[20px] font-bold flex items-center gap-[10px] m-0 text-gray-900">
              <AlertTriangle size={22} color="#ef4444" strokeWidth={2} /> 
              Usuarios Denunciados
            </h1>
            <p className="text-xs text-gray-500 mt-1 m-0">
              {pagination ? `${pagination.totalItemCount} usuarios con reportes en la plataforma` : "Cargando..."}
            </p>
          </div>
        </div>

        {/* ÁREA SCROLLABLE */}
        <div className="flex-1 overflow-y-auto bg-gray-50 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          
          <div className="sticky top-0 z-10 shadow-sm bg-white">
            <ReportedUserFilterToolbar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              sortOrder={sortOrder}
              onSortChange={(val) => { setSortOrder(val); setCurrentPage(1); }}
            />
          </div>

          <div className="p-4 sm:p-5 md:p-7">
            <ReportedUserGrid
              reportedUsers={reportedUsers}
              loading={loading}
              onUserClick={openDrawer}
              onActionClick={openActionModal}
            />

            {/* Paginación */}
            {pagination && pagination.totalPageCount > 1 && (
              <div className="flex justify-center items-center gap-3 mt-8 pt-5 border-t border-gray-200">
                <button
                  disabled={!pagination.hasPrevious}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  Anterior
                </button>
                <span className="text-xs text-gray-500 font-medium">Página {pagination.currentPage} de {pagination.totalPageCount}</span>
                <button
                  disabled={!pagination.hasNext}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MODAL */}
      <UserComplaintsModal
        isOpen={modalOpen}
        userId={selectedUserId}
        userName={selectedUserName}
        onClose={() => {
          setModalOpen(false);
          setSelectedUserId(null);
          setSelectedUserName(null);
        }}
      />

    </div>
  );
};

export default ReportedUsersPage;